/* TeamAccept · public landing for `/team/accept?token=...`
 *
 * 3-state magic-link invitee experience:
 *   🟢 No account exists      → "Welcome to {Company} — set your password"
 *   🟡 Has a verqo account    → "Welcome back, {Name} — sign in to accept"
 *   🔴 Account suspended/etc. → Error + route to support
 *
 * Fully white-label using the seller's logo + primary color from
 * tenant.branding (no verqo wordmark on the page).
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Loader2, AlertTriangle, Check, CheckCircle2, Eye, EyeOff,
  ShieldCheck, Mail, KeyRound, LifeBuoy, MessageSquareText, Phone,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

// ── Password strength meter ──────────────────────────────────────────────
function _scorePwd(p) {
  let s = 0;
  if (!p) return 0;
  if (p.length >= 8)  s += 1;
  if (p.length >= 12) s += 1;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s += 1;
  if (/[0-9]/.test(p)) s += 1;
  if (/[^A-Za-z0-9]/.test(p)) s += 1;
  return Math.min(s, 4);
}
function StrengthBar({ pwd }) {
  const score = _scorePwd(pwd);
  const labels = ["Too weak", "Weak", "OK", "Good", "Strong"];
  const colors = ["#e7e5e4", "#ef4444", "#f59e0b", "#10b981", "#10b981"];
  return (
    <div className="mt-1.5" data-testid="pwd-strength">
      <div className="flex gap-1 h-1">
        {[0,1,2,3].map(i => (
          <div key={i} className="flex-1 rounded-full transition-colors" style={{
            background: i < score ? colors[score] : "#e7e5e4",
          }}/>
        ))}
      </div>
      <div className="text-[11px] mt-1" style={{ color: pwd ? colors[score] : "#737373" }}>
        {pwd ? labels[score] : "8+ chars · mix of letters, numbers, symbols"}
      </div>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────
function _absolute(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND}${url.startsWith("/") ? "" : "/"}${url}`;
}
function _contrast(hex) {
  try {
    let h = (hex || "#0d0d0d").replace("#","");
    if (h.length === 3) h = h.split("").map(c => c+c).join("");
    const r = parseInt(h.slice(0,2), 16);
    const g = parseInt(h.slice(2,4), 16);
    const b = parseInt(h.slice(4,6), 16);
    return (r + g + b) < 380 ? "#ffffff" : "#0d0d0d";
  } catch { return "#ffffff"; }
}
function _firstName(full) {
  if (!full) return null;
  return String(full).trim().split(/\s+/)[0];
}
function _formatJoinDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "long", year: "numeric" });
  } catch { return null; }
}

// ── Page shell (logo header + body) ──────────────────────────────────────
function BrandShell({ logo, primary, company, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6" style={{ background: "#f5f5f4" }} data-testid="team-accept-page">
      <div className="w-full max-w-md bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-7 pt-7 pb-2">
          {logo ? (
            <img src={logo} alt={company} className="h-10 max-w-[180px] object-contain" data-testid="brand-logo"/>
          ) : (
            <div className="text-lg font-semibold tracking-tight" style={{ color: primary }}>{company}</div>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────
export default function TeamAccept() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [state, setState] = useState({ loading: true, invite: null, error: null });
  const [name, setName]         = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone]       = useState("");
  const [showPwd, setShowPwd]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr]   = useState(null);
  const [resetSending, setResetSending] = useState(false);
  const [resetSent, setResetSent]   = useState(false);
  // OTP sub-state (Feb 2026, Flavor C): after the password+phone submit
  // returns `needs_otp: true`, we show an OTP entry screen instead of
  // the form. `otpPending` holds the masked phone for display.
  const [otpPending, setOtpPending] = useState(null); // {phone_masked, expires_in}
  const [otpCode, setOtpCode]       = useState("");
  const [otpResending, setOtpResending] = useState(false);
  const [otpResentAt, setOtpResentAt]   = useState(null);

  useEffect(() => {
    if (!token) { setState({ loading: false, invite: null, error: "Missing invite token." }); return; }
    axios.get(`${API}/team/accept/precheck`, { params: { token } })
      .then(r => {
        setState({ loading: false, invite: r.data, error: null });
        if (r.data?.member_name && !r.data?.is_existing_user) setName(r.data.member_name);
      })
      .catch(e => {
        const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || "This invite is invalid or has expired.";
        setState({ loading: false, invite: null, error: msg });
      });
  }, [token]);

  const branding = state.invite?.tenant_branding || {};
  const primary  = useMemo(() => branding.primary_color || "#0d0d0d", [branding]);
  const ctaText  = useMemo(() => _contrast(primary), [primary]);
  const logo     = useMemo(() => _absolute(branding.logo_url), [branding.logo_url]);
  const company  = state.invite?.tenant_name || "the team";

  // Trigger a password reset for the EXISTING user. Reuses the standard
  // verqo password-reset flow — they get an email, click it, set a new
  // password, then come back and accept the invite.
  const sendReset = async () => {
    if (!state.invite?.member_email) return;
    setResetSending(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: state.invite.member_email });
      setResetSent(true);
    } catch (e) {
      // Backend intentionally always returns 200 to avoid user-enumeration;
      // we only land here on transport error.
      setResetSent(true);
    } finally {
      setResetSending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitErr(null);
    const isNew = state.invite?.existing_user_state == null;
    if (isNew && _scorePwd(password) < 2) {
      setSubmitErr("Please choose a stronger password (8+ chars).");
      return;
    }
    // Phone required for new users + for existing users whose phone isn't
    // verified yet. Existing users with `phone_verified=true` skip the
    // phone field entirely (server falls back to the verified number).
    const existing = state.invite?.existing_user || {};
    const phoneRequired = isNew || !existing.phone_verified;
    if (phoneRequired && !phone.trim()) {
      setSubmitErr("Phone number is required — we'll text you a 6-digit code to verify it.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/team/accept`, {
        token,
        password,
        name:  isNew ? name : undefined,
        phone: phoneRequired ? phone.trim() : undefined,
      });
      if (data.needs_otp) {
        // Move to OTP entry sub-state
        setOtpPending({ phone_masked: data.phone_masked, expires_in: data.expires_in });
        setSubmitting(false);
        return;
      }
      if (data.token) localStorage.setItem("token", data.token);
      window.location.assign(data.redirect_to || "/seller");
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
               || e?.response?.data?.detail
               || "Something went wrong. Please try again.";
      setSubmitErr(typeof msg === "string" ? msg : "Something went wrong.");
      setSubmitting(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setSubmitErr(null);
    if (!/^\d{4,8}$/.test(otpCode.trim())) {
      setSubmitErr("Enter the 6-digit code from your SMS.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/team/accept/verify-otp`, {
        token, code: otpCode.trim(),
      });
      if (data.token) localStorage.setItem("token", data.token);
      window.location.assign(data.redirect_to || "/seller");
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
               || e?.response?.data?.detail
               || "That code didn't work. Try again.";
      setSubmitErr(typeof msg === "string" ? msg : "Verification failed.");
      setSubmitting(false);
    }
  };

  const resendOtp = async () => {
    setOtpResending(true);
    try {
      await axios.post(`${API}/team/accept/resend-otp`, { token, code: "0000" });
      setOtpResentAt(Date.now());
    } catch (e) { /* swallow */ }
    finally { setOtpResending(false); }
  };

  // ── Render: loading ────────────────────────────────────────────────────
  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100 text-stone-700">
        <Loader2 className="w-5 h-5 animate-spin mr-2"/> Loading invite…
      </div>
    );
  }

  // ── Render: invite invalid / expired / revoked / already accepted ─────
  if (state.error || !state.invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100 p-6">
        <div className="bg-white border border-stone-200 rounded-2xl p-8 max-w-md w-full text-center shadow-sm" data-testid="invite-invalid">
          <div className="w-12 h-12 rounded-full bg-rose-50 border border-rose-200 inline-flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-rose-600"/>
          </div>
          <h1 className="text-xl font-semibold text-stone-900 mb-2">Invite unavailable</h1>
          <p className="text-sm text-stone-600 leading-relaxed">{state.error || "This invite link is invalid or has expired."}</p>
          <p className="text-[12px] text-stone-500 mt-4">Ask the person who invited you to resend it.</p>
        </div>
      </div>
    );
  }

  const invite = state.invite;
  const state3 = invite.existing_user_state; // null | "active" | "suspended" | "pending_deletion"

  // ── Render: 📱 OTP entry — shown when accept returned needs_otp ────────
  if (otpPending) {
    return (
      <BrandShell logo={logo} primary={primary} company={company}>
        <form onSubmit={submitOtp} className="px-7 pb-7 pt-3 space-y-5" data-testid="accept-state-otp">
          <div>
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider bg-sky-50 text-sky-800 border border-sky-200 mb-3">
              <MessageSquareText className="w-3 h-3"/> Almost there
            </div>
            <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">
              Enter the code we just texted you.
            </h1>
            <p className="mt-2 text-[14px] text-stone-600 leading-relaxed">
              We sent a 6-digit code to <strong className="text-stone-800">{otpPending.phone_masked}</strong>.
              It expires in 10 minutes.
            </p>
            <p className="mt-1.5 text-[12px] text-stone-500">
              Wrong number? <a href={window.location.href} className="underline hover:text-stone-800">Start over</a>.
            </p>
          </div>

          <div>
            <label className="text-[12px] text-stone-600 font-medium block mb-1.5">6-digit code</label>
            <Input
              value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 8))}
              placeholder="123456"
              required minLength={4} maxLength={8}
              inputMode="numeric" autoComplete="one-time-code"
              data-testid="accept-otp-code"
              className="bg-stone-50 border-stone-200 text-center text-2xl tracking-[0.4em] font-mono"
            />
          </div>

          {submitErr && (
            <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-[12.5px] text-rose-900 flex gap-2 items-start" data-testid="accept-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
              <div>{submitErr}</div>
            </div>
          )}

          <Button
            type="submit" disabled={submitting || otpCode.length < 4}
            className="w-full h-11 rounded-full font-semibold text-[14px]"
            style={{ background: primary, color: ctaText }}
            data-testid="accept-otp-submit"
          >
            {submitting
              ? <><Loader2 className="w-4 h-4 animate-spin mr-2"/>Verifying…</>
              : <>Verify & continue<Check className="w-4 h-4 ml-2"/></>}
          </Button>

          <div className="text-center">
            {otpResentAt ? (
              <span className="text-[11.5px] text-emerald-700 inline-flex items-center gap-1">
                <Check className="w-3 h-3"/> New code sent
              </span>
            ) : (
              <button type="button" onClick={resendOtp} disabled={otpResending}
                      className="text-[12px] text-stone-600 hover:text-stone-900 underline disabled:opacity-50"
                      data-testid="accept-otp-resend">
                {otpResending ? "Sending…" : "Didn't get the code? Resend"}
              </button>
            )}
          </div>
        </form>
      </BrandShell>
    );
  }

  // ── Render: 🔴 STATE 3 — suspended / pending deletion ──────────────────
  if (state3 === "suspended" || state3 === "pending_deletion") {
    const isSuspended = state3 === "suspended";
    return (
      <BrandShell logo={logo} primary={primary} company={company}>
        <div className="px-7 pb-7 pt-3" data-testid="accept-state-suspended">
          <div className="w-12 h-12 rounded-full bg-rose-50 border border-rose-200 inline-flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-rose-600"/>
          </div>
          <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">
            {isSuspended ? "Your verqo account is suspended" : "Your verqo account is pending deletion"}
          </h1>
          <p className="mt-3 text-[14px] text-stone-600 leading-relaxed">
            We can't add <strong className="text-stone-800">{invite.member_email}</strong> to{" "}
            <strong className="text-stone-800">{company}</strong>'s team while the account is in this state.
          </p>
          <p className="mt-2 text-[13px] text-stone-600 leading-relaxed">
            Please contact support to restore it. Your inviter,{" "}
            <strong className="text-stone-800">{invite.inviter_name}</strong>, can resend this invite once your account is back to normal.
          </p>
          <div className="mt-6 space-y-2">
            <a href="mailto:support@verqo.com?subject=Account%20suspended%20-%20team%20invite%20blocked"
               className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-full font-semibold text-[14px] transition-opacity hover:opacity-90"
               style={{ background: primary, color: ctaText }}
               data-testid="accept-contact-support">
              <LifeBuoy className="w-4 h-4"/> Contact verqo support
            </a>
            <div className="text-[11.5px] text-stone-500 text-center">
              Or email <a href="mailto:support@verqo.com" className="underline">support@verqo.com</a> directly.
            </div>
          </div>
        </div>
      </BrandShell>
    );
  }

  // ── Render: 🟡 STATE 2 — existing verqo account, must sign in ─────────
  if (state3 === "active") {
    const existing  = invite.existing_user || {};
    const firstName = _firstName(existing.name) || _firstName(invite.member_name);
    const joinDate  = _formatJoinDate(existing.created_at);
    return (
      <BrandShell logo={logo} primary={primary} company={company}>
        <form onSubmit={submit} className="px-7 pb-7 pt-3 space-y-5" data-testid="accept-state-existing">
          <div>
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 mb-3">
              <Mail className="w-3 h-3"/> Welcome back
            </div>
            <h1 className="text-2xl font-semibold text-stone-900 tracking-tight">
              Hi {firstName || "there"} — sign in to join {company}.
            </h1>
            <p className="mt-2 text-[14px] text-stone-600 leading-relaxed">
              You already have a verqo account
              {existing.company ? <> under <strong className="text-stone-800">{existing.company}</strong></> : null}
              {joinDate ? <> (registered {joinDate})</> : null}.{" "}
              <strong className="text-stone-800">{invite.inviter_name}</strong> just invited that account to{" "}
              <strong className="text-stone-800">{company}</strong> as a{" "}
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[12px] font-medium" style={{ background: `${primary}15`, color: primary }}>
                <ShieldCheck className="w-3 h-3"/>{invite.role_label}
              </span>.
            </p>
            <p className="mt-2 text-[12.5px] text-stone-500 leading-relaxed">
              Sign in with your existing verqo password — you'll keep your current account and also get team access at {company}.
            </p>
          </div>

          <div>
            <label className="text-[12px] text-stone-600 font-medium block mb-1.5">Your existing verqo password</label>
            <div className="relative">
              <Input
                type={showPwd ? "text" : "password"}
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Your current password" required minLength={8}
                data-testid="accept-existing-password"
                className="bg-stone-50 border-stone-200 pr-10"
              />
              <button type="button" onClick={() => setShowPwd(v => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-800 p-1.5"
                      aria-label="Toggle password visibility">
                {showPwd ? <EyeOff className="w-3.5 h-3.5"/> : <Eye className="w-3.5 h-3.5"/>}
              </button>
            </div>
            {/* Forgot password — fires a reset email, then user comes back */}
            <div className="mt-1.5 text-right">
              {resetSent ? (
                <span className="text-[11.5px] text-emerald-700 inline-flex items-center gap-1">
                  <Check className="w-3 h-3"/> Reset email sent — check {invite.member_email}
                </span>
              ) : (
                <button type="button" onClick={sendReset} disabled={resetSending}
                        className="text-[11.5px] text-stone-600 hover:text-stone-900 underline disabled:opacity-50"
                        data-testid="accept-forgot-password">
                  {resetSending ? "Sending…" : "Forgot your password?"}
                </button>
              )}
            </div>
          </div>

          {/* Phone field — shown ONLY if existing user has no verified phone */}
          {!existing.phone_verified && (
            <div>
              <label className="text-[12px] text-stone-600 font-medium block mb-1.5 inline-flex items-center gap-1.5">
                <Phone className="w-3 h-3 text-stone-500"/>Add a mobile phone for SMS-MFA
              </label>
              <Input
                type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="+1 555 123 4567" required
                inputMode="tel" autoComplete="tel"
                data-testid="accept-phone-existing"
                className="bg-stone-50 border-stone-200"
              />
              <p className="text-[11px] text-stone-500 mt-1">Required for team-member access. We'll text a 6-digit code to confirm.</p>
            </div>
          )}
          {existing.phone_verified && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 text-[12px] text-emerald-900 flex gap-2 items-start">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0"/>
              <div>Your verified phone ({existing.phone_masked || "on file"}) will also be used for SMS verification at {company}.</div>
            </div>
          )}

          {submitErr && (
            <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-[12.5px] text-rose-900 flex gap-2 items-start" data-testid="accept-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
              <div>{submitErr}</div>
            </div>
          )}

          <Button
            type="submit" disabled={submitting || password.length < 8}
            className="w-full h-11 rounded-full font-semibold text-[14px]"
            style={{ background: primary, color: ctaText }}
            data-testid="accept-submit"
          >
            {submitting
              ? <><Loader2 className="w-4 h-4 animate-spin mr-2"/>Joining…</>
              : <>Accept invite & continue<Check className="w-4 h-4 ml-2"/></>}
          </Button>

          {/* Skip-and-just-login escape hatch for returning users */}
          <div className="text-center">
            <a href="/login" className="text-[12px] text-stone-600 hover:text-stone-900 underline" data-testid="accept-go-to-login">
              Already have an account? Sign in →
            </a>
          </div>

          <p className="text-[11px] text-stone-500 text-center leading-relaxed">
            By accepting, you agree to {company}'s policies for using their wholesale portal.
          </p>
        </form>
      </BrandShell>
    );
  }

  // ── Render: 🟢 STATE 1 — brand new user, create password ───────────────
  return (
    <BrandShell logo={logo} primary={primary} company={company}>
      <form onSubmit={submit} className="px-7 pb-7 pt-3 space-y-5" data-testid="accept-state-new">
        <div>
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 mb-3">
            <Mail className="w-3 h-3"/> You're invited
          </div>
          <h1 className="text-2xl font-semibold text-stone-900 tracking-tight" data-testid="accept-headline">
            Welcome to {company}.
          </h1>
          <p className="mt-2 text-[14px] text-stone-600 leading-relaxed">
            <strong className="text-stone-800">{invite.inviter_name}</strong> invited you as a{" "}
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[12px] font-medium" style={{ background: `${primary}15`, color: primary }}>
              <ShieldCheck className="w-3 h-3"/>{invite.role_label}
            </span>. Set a password to access your portal.
          </p>
        </div>

        <div>
          <label className="text-[12px] text-stone-600 font-medium block mb-1.5">Your name</label>
          <Input
            value={name} onChange={e => setName(e.target.value)}
            placeholder="Jane Smith" required
            data-testid="accept-name"
            className="bg-stone-50 border-stone-200"
          />
        </div>

        <div>
          <label className="text-[12px] text-stone-600 font-medium block mb-1.5 inline-flex items-center gap-1.5">
            <KeyRound className="w-3 h-3 text-stone-500"/>Create your password
          </label>
          <div className="relative">
            <Input
              type={showPwd ? "text" : "password"}
              value={password} onChange={e => setPassword(e.target.value)}
              placeholder="8+ characters" required minLength={8}
              data-testid="accept-password"
              className="bg-stone-50 border-stone-200 pr-10"
            />
            <button type="button" onClick={() => setShowPwd(v => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-800 p-1.5"
                    aria-label="Toggle password visibility">
              {showPwd ? <EyeOff className="w-3.5 h-3.5"/> : <Eye className="w-3.5 h-3.5"/>}
            </button>
          </div>
          <StrengthBar pwd={password}/>
        </div>

        <div>
          <label className="text-[12px] text-stone-600 font-medium block mb-1.5 inline-flex items-center gap-1.5">
            <Phone className="w-3 h-3 text-stone-500"/>Mobile phone (we'll text a verification code)
          </label>
          <Input
            type="tel" value={phone} onChange={e => setPhone(e.target.value)}
            placeholder="+1 555 123 4567" required
            inputMode="tel" autoComplete="tel"
            data-testid="accept-phone"
            className="bg-stone-50 border-stone-200"
          />
          <p className="text-[11px] text-stone-500 mt-1">Include country code (e.g. +1 for US). We'll text a 6-digit code to confirm.</p>
        </div>

        {submitErr && (
          <div className="rounded-lg border border-rose-300 bg-rose-50 p-3 text-[12.5px] text-rose-900 flex gap-2 items-start" data-testid="accept-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
            <div>{submitErr}</div>
          </div>
        )}

        <Button
          type="submit" disabled={submitting || password.length < 8 || !name.trim() || !phone.trim()}
          className="w-full h-11 rounded-full font-semibold text-[14px]"
          style={{ background: primary, color: ctaText }}
          data-testid="accept-submit"
        >
          {submitting
            ? <><Loader2 className="w-4 h-4 animate-spin mr-2"/>Creating your account…</>
            : <>Continue<Check className="w-4 h-4 ml-2"/></>}
        </Button>

        {/* Escape hatch — in case the invitee remembers they already have a verqo account */}
        <div className="text-center">
          <a href="/login" className="text-[12px] text-stone-600 hover:text-stone-900 underline" data-testid="accept-go-to-login">
            Already have a verqo account? Sign in →
          </a>
        </div>

        <p className="text-[11px] text-stone-500 text-center leading-relaxed">
          Your email was verified by clicking this invite link. By creating an account, you agree to {company}'s policies for using their wholesale portal.
        </p>
      </form>
    </BrandShell>
  );
}
